import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'
import NotifyMeForm from 'src/components/Details/NotifyMeForm'
import { LocationType } from 'src/types/graphql'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

// Only legitimate "temporarily unavailable" states reach this banner.
// Truly-deleted listings (admin cleanup / junk data) are hard-filtered at
// the ES layer and fall through to the `buildLostListingRedirect` path in
// the [configId] page's getStaticProps — they redirect to search with the
// legacy <LostListingToast>, not inline.
export type UnavailableReason = 'paused' | 'sold_out'

interface UnavailableBannerProps {
  reason: UnavailableReason
  city?: string | null
  /**
   * DOM id of the SimilarLocations section to scroll to. The primary CTA
   * smooth-scrolls to this element so the renter immediately sees the
   * alternatives the page already loaded — instead of getting dumped into
   * raw search via a redirect (the pre-2026-05-21 behavior).
   */
  similarSectionId: string
  /**
   * When true, swap the CTA order so the WhatsApp escape hatch is primary
   * and "Ver espacios similares" is secondary. Set for Broker/Traditional
   * listings — those funnels are specialist-mediated (Fernando via the
   * corporate WA → LangGraph triage), so when the specific listing is gone
   * the next-best action is talking to the asesor, not browsing the
   * SimilarLocations rail. Monthly listings keep similars-first.
   */
  preferWhatsapp?: boolean
  /**
   * Build B — when present, renders the notify-me phone-capture form below the
   * CTAs, turning the dead-end into a demand signal (→ DemandWatch →
   * supply-arrival re-touch). Omitted for owner/admin previews.
   */
  notify?: {
    locationId: string
    configId: string
    locationType?: LocationType | null
    coordinates?: { lat: number; lng: number } | null
  }
}

const REASON_KEY: Record<UnavailableReason, 'PAUSED' | 'SOLD_OUT'> = {
  paused: 'PAUSED',
  sold_out: 'SOLD_OUT'
}

const UnavailableBanner = ({
  reason,
  city,
  similarSectionId,
  preferWhatsapp = false,
  notify
}: UnavailableBannerProps) => {
  const { t } = useTranslation('details')

  const key = REASON_KEY[reason]
  const eyebrow = t(`UNAVAILABLE.${key}.EYEBROW`)
  const title = t(`UNAVAILABLE.${key}.TITLE`)
  const subtitle = t(`UNAVAILABLE.${key}.SUBTITLE`)

  const waMessage = city
    ? t('UNAVAILABLE.WA_PREFILL_WITH_CITY', { city })
    : t('UNAVAILABLE.WA_PREFILL')
  const waHref = buildWhatsappUrl(waMessage)

  const handleSimilarClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const el = document.getElementById(similarSectionId)
      if (!el) return
      e.preventDefault()
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [similarSectionId]
  )

  // CTA renderers — extracted so we can swap order based on `preferWhatsapp`
  // without duplicating markup. The "primary" / "secondary" classes are
  // assigned at render time based on which slot each one lands in.
  const similarCta = (variantClass: string) => (
    <a
      key="similar"
      href={`#${similarSectionId}`}
      onClick={handleSimilarClick}
      className={variantClass}
    >
      {t('UNAVAILABLE.SIMILAR_CTA')}
    </a>
  )
  const waCta = (variantClass: string) => (
    <a
      key="wa"
      href={waHref}
      target="_blank"
      rel="noopener noreferrer"
      className={`${variantClass} gap-2`}
    >
      <i className="si si-chat text-base" />
      {t('UNAVAILABLE.WA_CTA')}
    </a>
  )

  const primaryClass =
    'inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-center text-[14px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] md:text-[15px]'
  const secondaryClass =
    'inline-flex items-center justify-center border border-ink-100/40 px-6 py-3 text-center text-[14px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500 md:text-[15px]'

  return (
    // Mobile listing page has no in-flow nav — MobileNavbar floats as an
    // absolute overlay (z-10000) over the gallery, so without clearance
    // this banner would sit under the close/share buttons. Push it down by
    // `--nav-h` + a touch of breathing room. Desktop already has 90px on
    // the container above, so no extra spacing there.
    //
    // PADDING on a wrapper, not margin-top on the <aside>: the banner is
    // the first in-flow child of the page, so a margin-top collapsed
    // through every ancestor and shifted the positioned container the
    // MobileNavbar anchors to — the nav moved DOWN with the banner and its
    // back button sat on the "SIN DISPONIBILIDAD" eyebrow (screenshot-
    // confirmed 2026-09-05). Padding never collapses.
    <div className="pt-[calc(var(--nav-h)+12px)] md:pt-0">
      <aside
        role="status"
        aria-live="polite"
        className="mb-6 border border-ink-100/15 bg-cream-300 p-6 md:mb-8 md:p-8"
      >
        <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          {eyebrow}
        </p>
        <h2 className="mt-2 font-display text-[22px] leading-[1.15] font-medium text-ink-700 md:text-[28px]">
          {title}
        </h2>
        <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {subtitle}
        </p>

        <div className="mt-5 flex flex-col gap-2 md:mt-6 md:flex-row md:items-center md:gap-3">
          {preferWhatsapp
            ? [waCta(primaryClass), similarCta(secondaryClass)]
            : [similarCta(primaryClass), waCta(secondaryClass)]}
        </div>

        {notify && (
          <NotifyMeForm
            locationId={notify.locationId}
            configId={notify.configId}
            locationType={notify.locationType}
            coordinates={notify.coordinates}
            city={city}
          />
        )}
      </aside>
    </div>
  )
}

export default UnavailableBanner
